<script setup lang="ts">
    import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
    import type { HTMLAttributes } from 'vue';
    import { reactiveOmit } from '@vueuse/core';
    import { DialogContent, DialogPortal, useForwardPropsEmits } from 'reka-ui';
    import { cn } from '../utils';
    import SheetOverlay from './SheetOverlay.vue';

    interface SheetContentProps extends DialogContentProps {
        class?: HTMLAttributes['class'];
        side?: 'top' | 'right' | 'bottom' | 'left';
    }

    const props = withDefaults(defineProps<SheetContentProps>(), {
        side: 'right',
    });
    const emits = defineEmits<DialogContentEmits>();

    defineOptions({ inheritAttrs: false });

    const delegatedProps = reactiveOmit(props, 'class', 'side');

    const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <DialogPortal>
        <SheetOverlay />
        <DialogContent
            data-slot="sheet-content"
            :class="
                cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
                    side === 'right' &&
                        'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[320px] lg:w-130 border-l',
                    side === 'left' &&
                        'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-[320px] lg:w-130 border-r ',
                    side === 'top' && 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
                    side === 'bottom' && 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
                    props.class,
                )
            "
            v-bind="{ ...$attrs, ...forwarded }"
        >
            <slot />
        </DialogContent>
    </DialogPortal>
</template>
